import { API_URL } from './api'; // Your base API URL

/**
 * A helper function to handle fetch requests, based on your api.js reference.
 * It includes error handling, 401 redirection, and 204 (No Content) management.
 */

const fetchWithHandling = async (url, options) => {
  try {
    options.credentials = 'include';
    const response = await fetch(url, options);

    if (response.status === 401) {
      // Handle token expiry
      localStorage.clear();
      window.location.href = '/login';
      throw new Error('Unauthorized: Token expired or invalid.');
    }

    if (!response.ok) {
      // For non-OK responses, try to parse a JSON error body
      const errorDetails = await response.json().catch(() => ({}));
      throw new Error(
        errorDetails.error ||
          errorDetails.detail ||
          errorDetails.message ||
          `Error: ${response.status} ${response.statusText}`,
      );
    }

    if (response.status === 204) {
      // For DELETE requests or other "No Content" success responses
      return;
    }

    // For all other successful (200, 201) responses
    return response.json();
  } catch (error) {
    throw error; // Propagate the error for the component to catch
  }
};

// --- Meeting Endpoints ---
export const listMeetings = async (token) => {
  return fetchWithHandling(`${API_URL}/meetings/`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const createMeeting = async (token, meetingData) => {
  return fetchWithHandling(`${API_URL}/meetings/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(meetingData),
  });
};

export const updateMeeting = async (token, meetingId, meetingData) => {
  return fetchWithHandling(`${API_URL}/meetings/${meetingId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(meetingData),
  });
};

export const deleteMeeting = async (token, meetingId) => {
  return fetchWithHandling(`${API_URL}/meetings/${meetingId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
};

// --- Meeting Event Endpoints ---
export const createMeetingEvent = async (token, meetingId, date) => {
  return fetchWithHandling(`${API_URL}/meetings/${meetingId}/events/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ date: date }),
  });
};

export const deleteMeetingEvent = async (token, meetingId, eventId) => {
  return fetchWithHandling(
    `${API_URL}/meetings/${meetingId}/events/${eventId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

export const processMeetingEvent = async (token, meetingId, eventId) => {
  return fetchWithHandling(
    `${API_URL}/meetings/${meetingId}/events/${eventId}/process`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      // No body is needed, but you could add one if required
    },
  );
};

// --- Transcript Endpoints ---
export const addTranscript = async (token, meetingId, eventId, vttFile) => {
  const formData = new FormData();
  formData.append('file', vttFile, vttFile.name);

  return fetchWithHandling(
    `${API_URL}/meetings/${meetingId}/events/${eventId}/transcripts/`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // 'Content-Type' is NOT set here; the browser handles it for FormData
      },
      body: formData,
    },
  );
};

export const deleteTranscript = async (
  token,
  meetingId,
  eventId,
  transcriptId,
) => {
  return fetchWithHandling(
    `${API_URL}/meetings/${meetingId}/events/${eventId}/transcripts/${transcriptId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

// --- User Endpoint ---
export const listUsers = async (token) => {
  return fetchWithHandling(`${API_URL}/users/`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
};
